import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '../../../auth/AuthProvider';
import { markFirstMachineActivationSession } from '../../../auth/first-machine-activation-session';
import { permissions } from '../../../auth/permissions';
import { MachineCreateForm } from '../components/MachineCreateForm';
import { MachineLifecycleDialog } from '../components/MachineLifecycleDialog';
import { MachineRow } from '../components/MachineRow';
import { useRestoreMachine } from '../api/useMachineLifecycle';
import { useMachineDirectory } from '../api/useMachines';
import { IncrementalListFooter } from '../../../components/ui/IncrementalListFooter';
import type { MachineSummary } from '../types/machine';
import { machineLifecycleGuidance, type MachineLifecycleNotice } from '../lifecycle-guidance';

export function MachinesPage() {
  const [search, setSearch] = useState('');
  const [creatingFirstMachine, setCreatingFirstMachine] = useState(false);
  const [pendingRestore, setPendingRestore] = useState<MachineSummary | null>(null);
  const [restoreNotice, setRestoreNotice] = useState<MachineLifecycleNotice | null>(null);
  const firstMachineFormRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const auth = useAuth();
  const showingArchived = new URLSearchParams(location.search).get('status') === 'archived';
  const activeMachines = useMachineDirectory(showingArchived ? '' : search, 'ACTIVE');
  const archivedMachines = useMachineDirectory(showingArchived ? search : '', 'ARCHIVED');
  const machines = showingArchived ? archivedMachines : activeMachines;
  const restoreMachine = useRestoreMachine();
  const navigationNotice = normalizeLifecycleNotice((location.state as { machineLifecycleNotice?: MachineLifecycleNotice | string } | null)?.machineLifecycleNotice);
  const emptyOrganization = !search
    && !activeMachines.isLoading
    && !activeMachines.isError
    && !archivedMachines.isLoading
    && !archivedMachines.isError
    && (activeMachines.data?.machines.length ?? 0) === 0
    && (archivedMachines.data?.machines.length ?? 0) === 0;
  const hasArchivedMachines = (archivedMachines.data?.machines.length ?? 0) > 0;
  // customer_admin is defined as the full-access bootstrap role. Keep the first
  // activation action discoverable for that role even while permission context
  // is settling after the invited user's first successful sign-in. The API
  // remains authoritative and still enforces machine:create server-side.
  const canCreateFirstMachine =
    auth.can(permissions.machineCreate) || Boolean(auth.activeMembership?.role_ids.includes('customer_admin'));

  useEffect(() => {
    if (emptyOrganization && auth.activeCustomerId) {
      markFirstMachineActivationSession(auth.activeCustomerId);
    }
  }, [auth.activeCustomerId, emptyOrganization]);

  function beginFirstMachine() {
    setCreatingFirstMachine(true);
    window.requestAnimationFrame(() => firstMachineFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
  }

  async function confirmRestore() {
    if (!pendingRestore) return;
    try {
      await restoreMachine.mutateAsync(pendingRestore.id);
      setRestoreNotice(machineLifecycleGuidance('restore', pendingRestore.name));
      setPendingRestore(null);
    } catch {
      // Mutation state supplies the dialog error while the user decides whether to retry.
    }
  }

  if (emptyOrganization) {
    return (
      <section className="r42-page r42-page--wide r42-first-run-page">
        <div className="r42-first-run-hero">
          <span className="r42-eyebrow">Get started</span>
          <h1>Start with one machine</h1>
          <p className="r42-first-run-lede">
            Route42 organizes everything it learns around a machine. Choose the first piece of equipment your team wants to understand.
          </p>
        </div>

        <div className="r42-onboarding-path" aria-label="How Route42 starts building machine intelligence">
          <article className="r42-onboarding-primary">
            <div className="r42-onboarding-primary__top">
              <span className="r42-onboarding-primary__stage"><b>1</b> Start here</span>
              <span className="r42-onboarding-primary__icon"><i className="bi bi-gear-wide-connected" aria-hidden="true" /></span>
            </div>
            <h2>Create your first machine</h2>
            <p>
              Every Route42 workspace begins with a machine. Pick the first piece of equipment your team wants Route42 to learn about.
            </p>
            <div className="r42-onboarding-primary__examples">Pump <span>·</span> Conveyor <span>·</span> CNC <span>·</span> Boiler</div>

            <div className="r42-onboarding-primary__action">
              <button
                className="btn r42-first-run-cta"
                type="button"
                onClick={beginFirstMachine}
                disabled={!canCreateFirstMachine}
              >
                {creatingFirstMachine ? 'Continue machine setup' : 'Create your first machine'}
                <i className={`bi ${creatingFirstMachine ? 'bi-arrow-down' : 'bi-arrow-right'}`} aria-hidden="true" />
              </button>
              <small>
                {canCreateFirstMachine
                  ? 'Only the machine name is required. You can add details later.'
                  : 'Machine creation requires organization administrator or machine-create access.'}
              </small>
            </div>
          </article>

          <div className="r42-onboarding-next">
            <div className="r42-onboarding-next__lead" aria-hidden="true">
              <span />
              <i className="bi bi-arrow-down" />
            </div>
            <span className="r42-eyebrow">What happens next</span>
            <div className="r42-onboarding-secondary-flow">
              <OnboardingStep
                number="2"
                icon="bi-file-earmark-text"
                eyebrow="Next"
                title="Add what you know"
                description="Give Route42 the machine history your team already has."
                example="Manuals, logs, inspections, technician notes…"
              />
              <span className="r42-onboarding-arrow" aria-hidden="true"><i className="bi bi-arrow-right" /></span>
              <OnboardingStep
                number="3"
                icon="bi-stars"
                eyebrow="Then"
                title="Ask and investigate"
                description="Use the connected evidence to understand what is happening and why."
                example="Questions, evidence, investigations, machine memory."
              />
            </div>
          </div>
        </div>

        {creatingFirstMachine ? (
          <div className="r42-first-machine-form-wrap" ref={firstMachineFormRef}>
            <div className="r42-first-machine-form-head">
              <span className="r42-eyebrow">First machine</span>
              <h2>Create your first machine</h2>
              <p>Start with the name your team already uses. Everything else can be added now or later.</p>
            </div>
            <MachineCreateForm
              variant="first-machine"
              onCancel={() => setCreatingFirstMachine(false)}
              afterCreatePath={(machineId) => `/machines/${machineId}/documents?onboarding=first-machine`}
            />
          </div>
        ) : null}
      </section>
    );
  }

  const lifecycleNotice = restoreNotice ?? navigationNotice;
  const activeDirectoryEmpty = !activeMachines.isLoading && !activeMachines.isError && (activeMachines.data?.machines.length ?? 0) === 0;
  const resolvingArchivedCheck = !showingArchived && !search && activeDirectoryEmpty && archivedMachines.isLoading;
  const archivedLookupUnavailable = !showingArchived && !search && activeDirectoryEmpty && archivedMachines.isError && !archivedMachines.data;
  const noResults = !machines.isLoading && !machines.isError && !resolvingArchivedCheck && !archivedLookupUnavailable && (machines.data?.machines.length ?? 0) === 0;
  const restoreError = restoreMachine.error instanceof Error ? restoreMachine.error.message : null;

  return (
    <section className="r42-page r42-page--wide">
      <div className="r42-page-header">
        <div>
          <span className="r42-eyebrow">Route42</span>
          <h1>Machines</h1>
          <p>{showingArchived ? 'Restore machines your team wants to work with again.' : 'Select the machine you want Route42 to work with.'}</p>
        </div>
        {auth.can(permissions.machineCreate) ? (
          <Link className="btn r42-btn-primary" to="/machines/new">
            <i className="bi bi-plus-lg" aria-hidden="true" /> Add machine
          </Link>
        ) : null}
      </div>

      {lifecycleNotice ? (
        <div className="r42-machine-lifecycle-notice" role="status">
          <i className="bi bi-check-circle" aria-hidden="true" />
          <div><strong>{lifecycleNotice.title}</strong><span>{lifecycleNotice.body}</span></div>
        </div>
      ) : null}

      <nav className="r42-machine-status-tabs" aria-label="Machine status">
        <Link className={!showingArchived ? 'is-active' : ''} to="/machines" aria-current={!showingArchived ? 'page' : undefined}>Active</Link>
        <Link className={showingArchived ? 'is-active' : ''} to="/machines?status=archived" aria-current={showingArchived ? 'page' : undefined}>Archived</Link>
      </nav>

      <div className="r42-list-toolbar">
        <label className="r42-search">
          <i className="bi bi-search" aria-hidden="true" />
          <span className="visually-hidden">Search {showingArchived ? 'archived ' : ''}machines</span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={showingArchived ? 'Search archived machines...' : 'Search machines...'}
          />
        </label>
      </div>

      <div className="r42-list-panel" aria-live="polite">
        {machines.isLoading || resolvingArchivedCheck ? <MachineListSkeleton /> : null}
        {(machines.isError && !machines.data) || archivedLookupUnavailable ? (
          <div className="r42-inline-state r42-inline-state--error">
            <strong>{archivedLookupUnavailable ? 'Route42 couldn’t confirm whether archived machines exist.' : 'Route42 couldn’t load the machine list.'}</strong>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => void (archivedLookupUnavailable ? archivedMachines.refetch() : machines.refetch())}>
              Try again
            </button>
          </div>
        ) : null}
        {noResults ? (
          <MachineDirectoryEmptyState
            search={search}
            showingArchived={showingArchived}
            hasArchivedMachines={hasArchivedMachines}
            canCreate={auth.can(permissions.machineCreate)}
          />
        ) : null}
        {machines.data?.machines.map((machine) => (
          <MachineRow
            key={machine.id}
            machine={machine}
            canRestore={auth.can(permissions.machineRestore)}
            restoring={restoreMachine.isPending && pendingRestore?.id === machine.id}
            onRestore={setPendingRestore}
          />
        ))}
      </div>
      {machines.data?.machines.length ? (
        <IncrementalListFooter
          label="machines" loaded={machines.data.machines.length}
          hasMore={Boolean(machines.hasNextPage)}
          loading={machines.isFetchingNextPage}
          error={machines.isFetchNextPageError}
          onLoadMore={() => void machines.fetchNextPage()}
        />
      ) : null}

      {pendingRestore ? (
        <MachineLifecycleDialog
          action="restore"
          machine={pendingRestore}
          busy={restoreMachine.isPending}
          error={restoreError}
          onCancel={() => { if (!restoreMachine.isPending) setPendingRestore(null); }}
          onConfirm={() => void confirmRestore()}
        />
      ) : null}
    </section>
  );
}

function MachineDirectoryEmptyState({ search, showingArchived, hasArchivedMachines, canCreate }: {
  search: string;
  showingArchived: boolean;
  hasArchivedMachines: boolean;
  canCreate: boolean;
}) {
  if (search) {
    return (
      <div className="r42-empty-state">
        <i className="bi bi-search" aria-hidden="true" />
        <h2>No matching machines</h2>
        <p>Try a different machine-name prefix.</p>
      </div>
    );
  }
  if (showingArchived) {
    return (
      <div className="r42-empty-state">
        <i className="bi bi-archive" aria-hidden="true" />
        <h2>No archived machines</h2>
        <p>Machines you archive will stay recoverable here with their Machine Memory and history preserved.</p>
        <Link className="btn btn-outline-secondary" to="/machines">View active machines</Link>
      </div>
    );
  }
  return (
    <div className="r42-empty-state">
      <i className="bi bi-gear-wide-connected" aria-hidden="true" />
      <h2>No active machines</h2>
      <p>{hasArchivedMachines ? 'Your machine knowledge is still safe. Restore an archived machine to resume work.' : 'Add a machine when your team is ready to start building machine intelligence.'}</p>
      <div className="r42-empty-state__actions">
        {hasArchivedMachines ? <Link className="btn r42-btn-primary" to="/machines?status=archived">View archived machines</Link> : null}
        {canCreate ? <Link className="btn btn-outline-secondary" to="/machines/new">Add machine</Link> : null}
      </div>
    </div>
  );
}

function normalizeLifecycleNotice(value: MachineLifecycleNotice | string | undefined): MachineLifecycleNotice | null {
  if (!value) return null;
  return typeof value === 'string' ? { title: 'Machine lifecycle updated', body: value } : value;
}

function OnboardingStep({
  number,
  icon,
  eyebrow,
  title,
  description,
  example,
}: {
  number: string;
  icon: string;
  eyebrow: string;
  title: string;
  description: string;
  example: string;
}) {
  return (
    <article className="r42-onboarding-step r42-onboarding-step--secondary">
      <div className="r42-onboarding-step__top">
        <span className="r42-onboarding-step__number">{number}</span>
        <span className="r42-onboarding-step__icon"><i className={`bi ${icon}`} aria-hidden="true" /></span>
      </div>
      <span className="r42-onboarding-step__eyebrow">{eyebrow}</span>
      <strong>{title}</strong>
      <p>{description}</p>
      <small>{example}</small>
    </article>
  );
}

function MachineListSkeleton() {
  return (
    <div className="r42-skeleton-stack" aria-label="Loading machines">
      {[0, 1, 2].map((item) => (
        <div className="r42-skeleton-row" key={item}>
          <span />
          <div>
            <b />
            <small />
          </div>
        </div>
      ))}
    </div>
  );
}
